/**
 * 项目管理 API
 *
 * @description 提供项目相关的 CRUD 操作和业务逻辑接口
 */

import { request } from '@umijs/max';

/**
 * 项目信息
 */
export interface Project {
  /** 数字 ID */
  id: string;
  /** 业务唯一标识 */
  uuid: string;
  /** 项目名称 */
  name: string;
  /** 关联职位 UUID */
  jobUuid: string;
  /** 关联客户 UUID */
  clientUuid: string;
  /** 负责人 UUID */
  recruiterUuid: string;
  /** 当前状态 */
  status: string;
  /** 暂停或取消原因 */
  statusReason?: string | null;
  /** 优先级 */
  priority: string;
  /** 开始时间 */
  startDate?: string;
  /** 结束时间 */
  endDate?: string;
  /** 预算 */
  budget?: number;
  /** 项目费用 */
  fee?: number;
  /** 服务费比例（百分比，0-100） */
  feeRate?: number;
  /** 备注 */
  notes?: string;
  /** 创建时间 */
  createdAt?: string;
  /** 更新时间 */
  updatedAt?: string;
  /** 职位标题 */
  jobTitle?: string;
  /** 客户名称 */
  clientName?: string;
  /** 客户城市（来自 luole_hunter_client.city） */
  city?: string;
  /** 招聘顾问名称 */
  recruiterName?: string;
  /** 候选人数（兼容字段，优先使用 totalCandidates） */
  candidatesCount?: number;
  /** 总候选人数（后端字段名） */
  totalCandidates?: number;
  /** 成功候选人数（兼容字段，优先使用 successCandidates） */
  successCount?: number;
  /** 成功候选人数（后端字段名） */
  successCandidates?: number;
  /** 推荐数量（统计字段，等于总候选人数） */
  recommendationCount?: number;
  /** 面试数量（统计字段） */
  interviewCount?: number;
  /** 最新 Offer 为已发放或已接受的候选人数，后续阶段仍计入 */
  offerCount?: number;
  /** 有业绩分配记录的候选人数，同一候选人多笔分配只计一次 */
  performanceAllocationCount?: number;
  /** 创建者 UUID */
  createdByUuid?: string;
  /** 项目单一权限（创建者/负责人及其上级为 true） */
  hasAllPermission?: boolean;
  /** 是否可以维护回款管理（仅项目创建人） */
  canManageReceivables?: boolean;
  /** 客户所有者 UUID（用于判断私池权限） */
  clientOwnerUuid?: string | null;
  /** 关联轻猎头任务 UUID */
  lightHunterTaskUuid?: string | null;
  /** 关联轻猎头任务名称 */
  lightHunterTaskTitle?: string | null;
}

/**
 * 项目列表查询参数
 */
export interface ProjectListParams {
  /** 页码 */
  page?: number;
  /** 每页数量 */
  limit?: number;
  /** 每页数量（pageSize 同 limit，兼容旧参数） */
  pageSize?: number;
  /** 搜索关键词 */
  search?: string;
  /** 搜索关键词（keyword 同 search，兼容旧参数） */
  keyword?: string;
  /** 状态筛选 */
  status?: string;
  /** 优先级筛选 */
  priority?: string;
  /** 客户 UUID */
  clientUuid?: string;
  /** 招聘顾问 UUID */
  recruiterUuid?: string;
  /** 排序字段 */
  sortBy?:
    | 'updatedAt'
    | 'createdAt'
    | 'recentContact'
    | 'offerCount'
    | 'performanceAllocationCount';
  /** 排序方向 */
  sortOrder?: 'asc' | 'desc';
}

/**
 * 项目列表响应
 */
export interface ProjectListResponse {
  /** 列表数据 */
  data: Project[];
  /** 总记录数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

/**
 * 项目候选人
 */
export interface ProjectCandidate {
  /** 数字 ID */
  id: string;
  /** 业务唯一标识 */
  uuid: string;
  /** 项目 ID */
  projectId: string;
  /** 项目 UUID */
  projectUuid: string;
  /** 人才 ID */
  talentId: string;
  /** 人才 UUID */
  talentUuid: string;
  /** 当前阶段 */
  stage: string;
  /** 状态 */
  status: string;
  /** 评分 */
  score: number;
  /** 备注 */
  notes: string;
  /** Offer 日期 */
  offerDate?: string;
  /** 入职日期 */
  joinDate?: string;
  /** 推荐时间 */
  recommendedAt: string;
  /** 推荐人 */
  recommendedBy: string;
  /** 拒绝原因 */
  rejectionReason?: string;
  /** 客户反馈 */
  clientFeedback?: string;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
  /** 关联数据 - 人才姓名 */
  talentName?: string;
  /** 关联数据 - 人才电话 */
  talentPhone?: string;
  /** 关联数据 - 人才职位 */
  talentPosition?: string;
  /** 关联数据 - 创建人姓名（后端返回） */
  createdByName?: string | null;
  /** 审计字段 - 创建人UUID（后端返回） */
  createdByUuid?: string | null;
  /** 关联人才是否已从人才库删除（软删标记） */
  talentDeleted?: boolean;
  /** 是否来自轻猎头初筛入项 */
  fromLightHunter?: boolean;
  /** 人才所有者 UUID（用于判断私池权限） */
  talentOwnerUuid?: string | null;
  /** 人才所有者姓名 */
  talentOwnerName?: string | null;
  /** 候选人全部权限（由后端计算） */
  hasAllPermission?: boolean;
  /** 是否存在未删除的开票申请；存在时不能从项目移除，不区分申请或同步状态 */
  hasInvoiceApplication?: boolean;
  /** 是否存在未删除的业绩分配记录，与候选人当前阶段无关 */
  hasPerformanceAllocation?: boolean;
  /** 业绩分配及开票申请写权限（项目负责人/创建人及其组织上级） */
  canManageCommissionAllocation?: boolean;
  /** 可发起分配方案：具备业绩权限、处于允许阶段且有有效 Offer 收费信息 */
  canStartCommissionAllocation?: boolean;
  /** 当前用户是否可以设置黄单（需已有有效业绩分配） */
  canSetYellowOrder?: boolean;
  /** 最近一次 AI 匹配分（同 talent + job 维度） */
  aiMatchingScore?: number;
  /** 最近一次 AI 匹配状态 */
  aiMatchingStatus?: 'completed' | 'failed' | 'processing' | 'pending';
  /** 最近一次 AI 匹配更新时间 */
  aiMatchingUpdatedAt?: string;
  /** 回款摘要 */
  receivableSummary?: ProjectReceivableSummary | null;
  /** 后端根据当前业务阶段计算的允许流转目标 */
  allowedTransitions?: string[];
  /** 淘汰状态允许恢复到的原业务阶段 */
  restoreStage?: string | null;
  /** 淘汰状态恢复时应还原的原始状态 */
  restoreStatus?: string | null;
}

export type ReceivableStatus =
  | 'pending'
  | 'partial'
  | 'paid'
  | 'closed'
  | 'bad_debt';

export type ReceivablePaymentStatus = 'effective' | 'void';

export interface ProjectReceivable {
  id: string;
  uuid: string;
  projectUuid: string;
  candidateUuid: string;
  talentUuid?: string | null;
  clientUuid?: string | null;
  receivableAmount: number;
  status: ReceivableStatus;
  confirmedDate?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectReceivablePayment {
  id: string;
  uuid: string;
  receivableUuid: string;
  projectUuid: string;
  candidateUuid: string;
  amount: number;
  paidDate?: string | null;
  status: ReceivablePaymentStatus;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectReceivableSummary {
  receivable: ProjectReceivable;
  payments: ProjectReceivablePayment[];
  plans?: ProjectReceivablePlan[];
  receivableAmount: number;
  paidAmount: number;
  pendingAmount: number;
  paymentRate: number;
  lastPaidDate: string | null;
  status: ReceivableStatus;
  plannedAmount?: number;
  activePlannedAmount?: number;
  overdueAmount?: number;
  nextPlannedDate?: string | null;
  plannedCount?: number;
}

export interface ProjectReceivableTotals {
  totalReceivable: number;
  totalPaid: number;
  totalPending: number;
  paymentRate: number;
  totalPlanned?: number;
  totalActivePlanned?: number;
  totalOverdue?: number;
}

export interface ProjectReceivableLedger {
  items: ProjectReceivableSummary[];
  totals: ProjectReceivableTotals;
}

export interface UpdateProjectReceivableParams {
  receivableAmount?: number;
  status?: ReceivableStatus;
  confirmedDate?: string;
  dueDate?: string;
  notes?: string;
}

export interface CreateProjectReceivablePaymentParams {
  amount: number;
  paidDate?: string;
  status?: ReceivablePaymentStatus;
  notes?: string;
}

export interface UpdateProjectReceivablePaymentParams {
  amount?: number;
  paidDate?: string;
  status?: ReceivablePaymentStatus;
  notes?: string;
}

export type ReceivablePlanStatus =
  | 'pending'
  | 'reminded'
  | 'overdue'
  | 'fulfilled'
  | 'cancelled';

export interface ProjectReceivablePlan {
  id: string;
  uuid: string;
  receivableUuid: string;
  projectUuid: string;
  candidateUuid: string;
  plannedAmount: number;
  plannedDate: string;
  status: ReceivablePlanStatus;
  reminderDate?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProjectReceivablePlanParams {
  plannedAmount: number;
  plannedDate: string;
  status?: ReceivablePlanStatus;
  reminderDate?: string;
  notes?: string;
}

export interface UpdateProjectReceivablePlanParams {
  plannedAmount?: number;
  plannedDate?: string;
  status?: ReceivablePlanStatus;
  reminderDate?: string;
  notes?: string;
}

/**
 * 候选人状态变更历史
 */
export interface CandidateStatusHistory {
  /** 数字 ID */
  id: string;
  /** 业务唯一标识 */
  uuid: string;
  /** 项目候选人 UUID */
  projectCandidateUuid: string;
  /** 源状态 */
  fromStatus: string | null;
  fromStage?: string | null;
  /** 目标状态 */
  toStatus: string;
  toStage?: string | null;
  /** 表单类型（与业务阶段对应，如 performance_allocation） */
  formType?: string | null;
  /** 该次状态对应的表单数据快照 */
  payload?: Record<string, any> | null;
  /** 业务日期（如业绩日期、入职日期） */
  businessDate?: string | null;
  /** 备注 */
  notes?: string;
  /** 操作人姓名 */
  operatedByName?: string;
  /** 操作人 UUID */
  operatedByUuid?: string;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
}

/**
 * 更新候选人状态请求参数（支持带表单数据）
 */
export interface UpdateCandidateStatusParams {
  /** 新状态 */
  status: string;
  /** 业务阶段（以候选人状态下拉为准） */
  stage?: string;
  /** 备注 */
  notes?: string;
  /** 表单类型 */
  formType?: string;
  /** 表单数据快照 */
  payload?: Record<string, any>;
  /** 业务日期 */
  businessDate?: string;
}

/**
 * 编辑状态历史记录请求参数
 */
export interface UpdateCandidateStatusHistoryParams {
  notes?: string;
  payload?: Record<string, any>;
  businessDate?: string;
}

export interface CreateResumeRemarkParams {
  content: string;
}

/**
 * 项目详情（包含关联数据）
 */
export interface ProjectDetailData {
  /** 项目信息 */
  project: Project & {
    /** 成功候选人数 */
    successCandidates?: number;
    /** 总候选人数 */
    totalCandidates?: number;
    /** 客户名称 */
    clientName?: string;
    /** 职位标题 */
    jobTitle?: string;
    /** 招聘顾问名称 */
    recruiterName?: string;
  };
  /** 候选人列表 */
  candidates: ProjectCandidate[];
  /** 面试记录列表 */
  interviews: any[];
  /** 回款台账 */
  receivables?: ProjectReceivableLedger;
}

export interface ProjectJobBasicInfo {
  id: number;
  uuid: string;
  title: string;
  company: string | null;
  department: string | null;
  level: string | null;
  industry: string | null;
  function: string | null;
  location: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  description: string | null;
  requirements: string | null;
  status: string;
  priority: string;
  clientUuid: string | null;
  recruiterUuid: string | null;
  deadline: string | null;
  tags: string[] | null;
  createdAt: string;
  updatedAt: string;
  clientOwnerUuid: string | null;
}

/**
 * 构建项目查询参数
 *
 * @param params - 原始查询条件
 */
const buildProjectParams = (
  params?: ProjectListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    switch (key) {
      case 'pageSize':
        result.limit = value as number;
        break;
      case 'keyword':
        result.search = value as string;
        break;
      default:
        result[key] = value as string | number;
        break;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
};

export interface ProjectAiMatchItem {
  talentUuid: string;
  name: string;
  currentPosition?: string | null;
  location?: string | null;
  workExperience?: number | null;
  skills?: string[];
  ownerUuid?: string | null;
  isPublicPool: boolean;
  hasAllPermission: boolean;
  inProject: boolean;
  ruleScore: number;
  matchedKeywords: string[];
  aiStatus?: 'completed' | 'failed' | 'pending' | 'processing' | null;
  matchingScore?: number | null;
  decision?: string | null;
  summary?: string | null;
  profileSummary?: string | null;
}

export interface ProjectAiMatchPreview {
  projectUuid: string;
  jobUuid: string;
  jobTitle?: string;
  keywords?: string[];
  totalPreviewed: number;
  analyzedCount: number;
  pendingCount: number;
  items: ProjectAiMatchItem[];
}

export interface ProjectAiMatchList {
  projectUuid: string;
  jobUuid: string;
  items: ProjectAiMatchItem[];
}

export interface ProjectAiMatchRunResult {
  queued: number;
  skipped: number;
  failed: number;
  items: Array<{
    talentUuid: string;
    status?: string;
    matchingScore?: number | null;
    errorMessage?: string;
  }>;
}

/**
 * 项目管理 API
 */
export const projectApi = {
  /**
   * 获取项目列表
   *
   * @param params - 查询条件
   */
  getList: (params?: ProjectListParams) => {
    return request<ProjectListResponse>('/bff/luole-hunter/projects', {
      params: buildProjectParams(params),
    });
  },

  /**
   * 获取我创建的项目
   *
   * @param params - 查询条件
   */
  getMyProjects: (params?: ProjectListParams) => {
    return request<ProjectListResponse>(
      '/bff/luole-hunter/projects/my-projects',
      { params: buildProjectParams(params) },
    );
  },

  /**
   * 获取我推荐的项目
   *
   * @param params - 查询条件
   */
  getMyRecommendedProjects: (params?: ProjectListParams) => {
    return request<ProjectListResponse>(
      '/bff/luole-hunter/projects/my-recommended',
      { params: buildProjectParams(params) },
    );
  },

  /**
   * 获取我负责的项目
   *
   * @param params - 查询条件
   */
  getMyResponsibleProjects: (params?: ProjectListParams) => {
    return request<ProjectListResponse>(
      '/bff/luole-hunter/projects/my-responsible',
      { params: buildProjectParams(params) },
    );
  },

  /**
   * 获取项目详情
   *
   * @param uuid - 项目 UUID
   */
  getDetail: (uuid: string) => {
    return request<Project>(`/bff/luole-hunter/projects/${uuid}`);
  },

  /**
   * 获取项目完整信息（包含候选人、面试记录等）
   *
   * @param uuid - 项目 UUID
   */
  getDetailWithInfo: (uuid: string) => {
    return request<ProjectDetailData>(
      `/bff/luole-hunter/projects/${uuid}/details`,
    );
  },

  /**
   * 获取项目关联职位基础信息（无职位详情权限限制）
   *
   * @param projectUuid - 项目 UUID
   */
  getJobBasicInfo: (projectUuid: string) => {
    return request<ProjectJobBasicInfo>(
      `/bff/luole-hunter/projects/${projectUuid}/job-basic-info`,
    );
  },

  getReceivables: (projectUuid: string) => {
    return request<ProjectReceivableLedger>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables`,
    );
  },

  updateReceivable: (
    projectUuid: string,
    receivableUuid: string,
    data: UpdateProjectReceivableParams,
  ) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/${receivableUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  createReceivablePayment: (
    projectUuid: string,
    receivableUuid: string,
    data: CreateProjectReceivablePaymentParams,
  ) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/${receivableUuid}/payments`,
      {
        method: 'POST',
        data,
      },
    );
  },

  updateReceivablePayment: (
    projectUuid: string,
    paymentUuid: string,
    data: UpdateProjectReceivablePaymentParams,
  ) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/payments/${paymentUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  deleteReceivablePayment: (projectUuid: string, paymentUuid: string) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/payments/${paymentUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  createReceivablePlan: (
    projectUuid: string,
    receivableUuid: string,
    data: CreateProjectReceivablePlanParams,
  ) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/${receivableUuid}/plans`,
      {
        method: 'POST',
        data,
      },
    );
  },

  updateReceivablePlan: (
    projectUuid: string,
    planUuid: string,
    data: UpdateProjectReceivablePlanParams,
  ) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/plans/${planUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  remindReceivablePlan: (projectUuid: string, planUuid: string) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/plans/${planUuid}/remind`,
      {
        method: 'POST',
      },
    );
  },

  deleteReceivablePlan: (projectUuid: string, planUuid: string) => {
    return request<ProjectReceivableSummary>(
      `/bff/luole-hunter/projects/${projectUuid}/receivables/plans/${planUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * AI - 项目流程分析
   *
   * @param projectUuid - 项目 UUID
   */
  aiFlowAnalysis: (projectUuid: string) => {
    return request<{ content: string }>(
      `/bff/luole-hunter/projects/${projectUuid}/ai/flow-analysis`,
      {
        method: 'POST',
        data: {},
      },
    );
  },

  /**
   * AI - 项目智能建议
   *
   * @param projectUuid - 项目 UUID
   */
  aiSuggestions: (projectUuid: string) => {
    return request<{ suggestions: string[] }>(
      `/bff/luole-hunter/projects/${projectUuid}/ai/suggestions`,
      {
        method: 'POST',
        data: {},
      },
    );
  },

  /**
   * AI - 项目话术
   *
   * @param projectUuid - 项目 UUID
   * @param type - 话术类型
   */
  aiTalks: (
    projectUuid: string,
    type: 'actionPlan' | 'interview' | 'salary' | 'recovery',
  ) => {
    return request<{ items: string[] }>(
      `/bff/luole-hunter/projects/${projectUuid}/ai/talks`,
      {
        method: 'POST',
        data: { type },
      },
    );
  },

  /**
   * 获取项目面试记录列表
   *
   * @deprecated 请使用 projectInterviewApi.getByProject 代替
   * @param projectUuid - 项目 UUID
   */
  getInterviews: (projectUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/projects/${projectUuid}/interviews`,
    );
  },

  /**
   * 创建项目
   *
   * @param data - 项目数据
   */
  create: (data: Partial<Project>) => {
    return request<Project>('/bff/luole-hunter/projects', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新项目
   *
   * @param uuid - 项目 UUID
   * @param data - 更新内容
   */
  update: (uuid: string, data: Partial<Project>) => {
    return request<Project>(`/bff/luole-hunter/projects/${uuid}`, {
      method: 'PATCH',
      data,
    });
  },

  /**
   * 更新项目状态
   *
   * @param uuid - 项目 UUID
   * @param data - 目标状态及暂停/取消原因
   */
  updateStatus: (uuid: string, data: { status: string; reason?: string }) => {
    return request<Project>(`/bff/luole-hunter/projects/${uuid}/status`, {
      method: 'POST',
      data,
    });
  },

  /**
   * 删除项目
   *
   * @param uuid - 项目 UUID
   */
  delete: (uuid: string) => {
    return request<Project>(`/bff/luole-hunter/projects/${uuid}`, {
      method: 'DELETE',
    });
  },

  /**
   * 批量删除项目
   *
   * @param projectUuids - 项目 UUID 数组
   */
  batchDelete: (projectUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; name?: string }>;
    }>('/bff/luole-hunter/projects/batch-delete', {
      method: 'POST',
      data: { projectUuids },
    });
  },

  /**
   * 获取项目候选人列表
   *
   * @param projectUuid - 项目 UUID
   */
  getCandidates: (projectUuid: string) => {
    return request<ProjectCandidate[]>(
      `/bff/luole-hunter/projects/${projectUuid}/candidates`,
    );
  },

  /**
   * 添加候选人到项目
   *
   * @param projectUuid - 项目 UUID
   * @param data - 候选人数据
   */
  addCandidate: (projectUuid: string, data: Partial<ProjectCandidate>) => {
    return request<ProjectCandidate>(
      `/bff/luole-hunter/projects/${projectUuid}/candidates`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 从收藏夹添加私池候选人，服务端重新校验收藏关系和操作权限
   */
  addCandidateFromCollection: (
    projectUuid: string,
    data: { collectionUuid: string; talentUuid: string },
  ) => {
    return request<ProjectCandidate>(
      `/bff/luole-hunter/projects/${projectUuid}/candidates/from-collection`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 更新候选人信息
   *
   * @param candidateUuid - 候选人 UUID
   * @param data - 更新数据
   */
  updateCandidate: (candidateUuid: string, data: Partial<ProjectCandidate>) => {
    return request<ProjectCandidate>(
      `/bff/luole-hunter/projects/candidates/${candidateUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 更新候选人状态
   *
   * @param candidateUuid - 候选人 UUID
   * @param statusOrParams - 新状态字符串，或完整参数对象
   * @param notes - 备注（当第一参数为 status 时可选）
   */
  updateCandidateStatus: (
    candidateUuid: string,
    statusOrParams: string | UpdateCandidateStatusParams,
    notes?: string,
  ) => {
    const body: UpdateCandidateStatusParams =
      typeof statusOrParams === 'string'
        ? { status: statusOrParams, notes }
        : statusOrParams;
    return request<Project>(
      `/bff/luole-hunter/projects/candidates/${candidateUuid}/status`,
      {
        method: 'POST',
        data: body,
      },
    );
  },

  /** 设置候选人黄单状态 */
  setCandidateYellowOrder: (candidateUuid: string, confirmed = true) => {
    return request<ProjectCandidate>(
      `/bff/luole-hunter/projects/candidates/${candidateUuid}/yellow`,
      {
        method: 'POST',
        data: { confirmed },
      },
    );
  },

  /**
   * 获取候选人状态变更历史
   *
   * @param candidateUuid - 候选人 UUID
   */
  getCandidateStatusHistory: (candidateUuid: string) => {
    return request<CandidateStatusHistory[]>(
      `/bff/luole-hunter/projects/candidates/${candidateUuid}/status-history`,
    );
  },

  /**
   * 编辑某条状态历史记录
   *
   * @param historyUuid - 状态历史记录 UUID
   * @param params - 要更新的字段
   */
  updateCandidateStatusHistory: (
    historyUuid: string,
    params: UpdateCandidateStatusHistoryParams,
  ) => {
    return request<CandidateStatusHistory>(
      `/bff/luole-hunter/projects/candidates/status-history/${historyUuid}`,
      {
        method: 'PATCH',
        data: params,
      },
    );
  },

  /**
   * 记录候选人反馈（写入简历备注）
   *
   * @param talentUuid - 人才 UUID
   * @param params - 反馈内容
   */
  recordCandidateFeedback: (
    talentUuid: string,
    params: CreateResumeRemarkParams,
  ) => {
    return request<any>(
      `/bff/luole-hunter/resume-remarks/talents/${talentUuid}`,
      {
        method: 'POST',
        data: params,
      },
    );
  },

  previewAiMatches: (
    projectUuid: string,
    params?: { limit?: number; onlyUnanalyzed?: boolean },
  ) => {
    return request<ProjectAiMatchPreview>(
      `/bff/luole-hunter/projects/${projectUuid}/ai-matches/preview`,
      { params },
    );
  },

  listAiMatches: (projectUuid: string) => {
    return request<ProjectAiMatchList>(
      `/bff/luole-hunter/projects/${projectUuid}/ai-matches`,
    );
  },

  runAiMatches: (
    projectUuid: string,
    data: {
      talentUuids?: string[];
      scope?: 'preview';
      limit?: number;
      forceRefresh?: boolean;
    },
  ) => {
    return request<ProjectAiMatchRunResult>(
      `/bff/luole-hunter/projects/${projectUuid}/ai-matches/run`,
      {
        method: 'POST',
        data,
      },
    );
  },
};
